import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import { tracked } from "@supergrain/kernel/react"
import UnifiedLessonSimpleReadOnly from "../components/planner/card-stacks/UnifiedLessonSimpleReadOnly"

interface Props {
  lesson: UnifiedCardStack
  lessonTitleClass?: string
  showOddMessageIfCardsAreNull?: boolean
  /**
   * Flag to tweak reactivity when we're passed a version of a lesson
   * instead of an actual lesson.
   */
  isVersion?: boolean
}

/**
 * Adapts the Ember lesson model passed from `Planner::CardStacks::LessonSimpleReadOnly`
 * into a supergrain document, then renders the framework-agnostic UnifiedLessonSimpleReadOnly.
 */
export default tracked(function LessonSimpleReadOnlyIsland({
  lesson,
  lessonTitleClass,
  showOddMessageIfCardsAreNull,
  isVersion,
}: Props) {
  if (isVersion) {
    // A version is a snapshot value, not a store document — there is nothing to look up by id
    // (the id would resolve the CURRENT lesson). Render the given data verbatim, keyed by
    // revision.
    return (
      <UnifiedLessonSimpleReadOnly
        lesson={lesson}
        lessonTitleClass={lessonTitleClass}
        showOddMessageIfCardsAreNull={showOddMessageIfCardsAreNull}
        key={lesson.attributes.version}
      />
    )
  } else {
    return (
      <ReactiveLessonSimpleReadOnly
        lesson={lesson}
        lessonTitleClass={lessonTitleClass}
        showOddMessageIfCardsAreNull={showOddMessageIfCardsAreNull}
      />
    )
  }
})

const ReactiveLessonSimpleReadOnly = tracked(function ({
  lesson,
  lessonTitleClass,
  showOddMessageIfCardsAreNull,
}: Props) {
  // The Ember model is a carrier for an id; read the supergrain copy by id.
  const lessonHandle = useDocument("card-stack", lesson.id)

  if (lessonHandle.status === "error") throw lessonHandle.error
  if (!hasResolvedDocumentValue(lessonHandle)) return null

  return (
    <UnifiedLessonSimpleReadOnly
      // The handle types by document type; the props already narrowed this id to a unified lesson.
      lesson={lessonHandle.value as UnifiedCardStack}
      lessonTitleClass={lessonTitleClass}
      showOddMessageIfCardsAreNull={showOddMessageIfCardsAreNull}
    />
  )
})
